import { CustomerModel } from 'src/app/models/customers/customer';
import { UserModel } from 'src/app/models/users/user';

/**
 * Erzeugt Auftragsnummern clientseitig (offline-fähig). Ticket #974.
 *
 * Zwei getrennte Nummern, damit Auftragsnummer und Personal-Laufzeitnummer
 * unterscheidbar sind:
 *
 * **Auftragsnummer** ({@link generate}): **`Personalnummer-Laufnummer`**
 * (z.B. `42-00001`).
 * - **Personalnummer** : Ziffern aus `user.remote_id` (Fallback `user.id`)
 * - **Laufnummer** : pro Mitarbeiter fortlaufender, lokal in `localStorage`
 *   gehaltener Zähler, links mit Nullen auf {@link SEQUENCE_DIGITS} Stellen
 *   aufgefüllt.
 *
 * Der Zähler wird rein lokal geführt (kein Server), daher offline-fähig – und
 * damit pro Gerät fortlaufend. Gegen Dubletten (Zähler-Reset, zweites Gerät)
 * prüft die Verwaltungs-Maske die Nummer online gegen den Server und zählt
 * bei einem Treffer weiter; für offline erzeugte Aufträge bleibt der
 * Personalnummer-Präfix die Trennung zwischen Mitarbeitern.
 *
 * Generieren und Zählerstand sind getrennt: {@link generate} berechnet die
 * Nummer nur (ohne Seiteneffekt), {@link persistSequence} schreibt den Zähler
 * in den `localStorage` – erst dann, wenn der Auftrag tatsächlich angelegt
 * wurde. So verbraucht bloßes Öffnen/Verlassen der Neuanlage keine Laufnummern.
 *
 * **Personal-Laufzeitnummer** ({@link generateRuntimeNumber}):
 * **`[Personalnummer][Code]`**, insgesamt höchstens {@link MAX_LENGTH} Ziffern
 * (z.B. `4290418337`).
 * - **Personalnummer** : Ziffern aus `user.remote_id` (Fallback `user.id`) – voll
 *   sichtbar als Präfix.
 * - **Code** : verbleibende Stellen, berechnet als mathematische Funktion aus
 *   `f(User, Zeitpunkt, Kunde)` und links mit Nullen aufgefüllt.
 *
 * Damit ist die Nummer praktisch eindeutig (Zeitpunkt in ms + Kunde + User) und
 * rein lokal berechenbar. Bei ungewöhnlich langer Personalnummer
 * (>= {@link MAX_LENGTH}) wird sie gekürzt, damit mindestens eine Code-Stelle
 * bleibt.
 */
export class OrderNumberHelper {
  /** Präfix des localStorage-Schlüssels für den Laufnummern-Zähler je Mitarbeiter */
  private static readonly SEQUENCE_KEY_PREFIX = 'orderNumberSequence_';
  /** Stellen der Laufnummer (Nullauffüllung) */
  private static readonly SEQUENCE_DIGITS = 5;
  /** Maximale Gesamtlänge der Personal-Laufzeitnummer in Ziffern. */
  private static readonly MAX_LENGTH = 10;
  /** Primzahl-Multiplikatoren, um User- und Kundenanteil zu streuen. */
  private static readonly USER_FACTOR = 1_000_003;
  private static readonly CUSTOMER_FACTOR = 999_983;

  /**
   * Generiert die nächste Auftragsnummer für den übergebenen User – ohne den
   * lokalen Zähler zu verändern. Der Zähler wird erst durch
   * {@link persistSequence} erhöht, wenn der Auftrag tatsächlich angelegt wurde.
   * @param user User, dessen Personalnummer die Nummer bildet
   * @param skip Anzahl zu überspringender Laufnummern (für Dubletten-Prüfungen:
   *             `skip = n` liefert den (n+1)-ten Kandidaten)
   * @returns Auftragsnummer im Format `Personalnummer-Laufnummer`
   */
  public static generate(user: UserModel, skip: number = 0): string {
    const personalNumber = this.personalNumber(user);
    const sequence = this.currentSequence(personalNumber) + 1 + skip;
    return `${personalNumber}-${String(sequence).padStart(this.SEQUENCE_DIGITS, '0')}`;
  }

  /**
   * Persistiert den Laufnummern-Zähler für eine zuvor über {@link generate}
   * erzeugte und tatsächlich verwendete Auftragsnummer im `localStorage`.
   * Nummern mit fremdem Personalnummern-Präfix oder ohne Laufnummer werden
   * ignoriert; der Zähler wird nie zurückgesetzt (nur erhöht).
   * @param user User, dessen Zähler fortgeschrieben wird
   * @param orderNumber verwendete Auftragsnummer im Format `Personalnummer-Laufnummer`
   */
  public static persistSequence(user: UserModel, orderNumber: string): void {
    const personalNumber = this.personalNumber(user);
    const [prefix, sequencePart] = (orderNumber ?? '').split('-');
    const sequence = parseInt(sequencePart ?? '', 10);
    if (prefix !== personalNumber || !Number.isFinite(sequence)) return;
    const current = this.currentSequence(personalNumber);
    localStorage.setItem(
      `${this.SEQUENCE_KEY_PREFIX}${personalNumber}`,
      String(Math.max(current, sequence)),
    );
  }

  /**
   * Generiert die Personal-Laufzeitnummer für den übergebenen User und Kunden.
   * @param user User, dessen Personalnummer den sichtbaren Präfix bildet
   * @param customer Kunde, für den der Auftrag erstellt wird (Teil der Eindeutigkeit)
   * @returns Personal-Laufzeitnummer im Format `[Personalnummer][Code]`
   */
  public static generateRuntimeNumber(user: UserModel, customer?: CustomerModel): string {
    let personalNumber = this.personalNumber(user);
    // Mindestens eine Code-Stelle sicherstellen (10-Stellen-Grenze).
    if (personalNumber.length > this.MAX_LENGTH - 1) {
      personalNumber = personalNumber.slice(0, this.MAX_LENGTH - 1);
    }
    const codeDigits = this.MAX_LENGTH - personalNumber.length;
    const code = this.code(user, customer, codeDigits);
    return `${personalNumber}${code}`;
  }

  /**
   * Personalnummer: Ziffern aus `remote_id`; ist diese leer/nicht numerisch,
   * wird die lokale `id` verwendet.
   */
  private static personalNumber(user: UserModel): string {
    const digits = String(user?.remote_id ?? '').replace(/\D/g, '');
    return digits.length > 0 ? digits : String(user?.id ?? 0);
  }

  /**
   * Liest den aktuellen (zuletzt persistierten) Zählerstand des Mitarbeiters.
   */
  private static currentSequence(personalNumber: string): number {
    const key = `${this.SEQUENCE_KEY_PREFIX}${personalNumber}`;
    const current = parseInt(localStorage.getItem(key) ?? '', 10);
    return Number.isFinite(current) ? current : 0;
  }

  /**
   * Berechnet den Code-Anteil aus `f(User, Zeitpunkt, Kunde)`, gekürzt auf `codeDigits`
   * Stellen und links mit Nullen aufgefüllt.
   */
  private static code(user: UserModel, customer: CustomerModel | undefined, codeDigits: number): string {
    const userNumber = Number(user?.id ?? 0);
    const customerNumber = Number(customer?.id ?? 0);
    const raw = userNumber * this.USER_FACTOR + customerNumber * this.CUSTOMER_FACTOR + Date.now();
    const value = raw % Math.pow(10, codeDigits);
    return String(value).padStart(codeDigits, '0');
  }
}
